import React from 'react'
import cn from 'clsx'
import InputWrap from './InputWrap'
import Input from './Input'
import styled from 'styled-components'
import { rem } from 'polished'
import { InputTextBase } from 'components/Luxkit/Typography/InputText'
import { DMY_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'

const StyledInput = styled(Input)`
  ${InputTextBase}
  width: 100%;
  border: none;
  background: transparent;
  transition: color 0.2s;
  padding: ${rem(12)};
  border-radius: ${props => props.theme.borderRadius.medium};
  color: ${props => props.theme.palette.neutral.one};

  &::-ms-clear {
    display: none;
  }

  &::placeholder {
    color: ${props => props.theme.palette.neutral.four};
  }

  &:disabled {
    cursor: not-allowed;

    &::placeholder {
      color: ${props => props.theme.palette.neutral.two};
    }
  }

  /*
    Browser things like auto fill and validation pop ups use the
    actual input element as their target. We want this to look like
    the whole element - so we need our input to take up the whole container

    When there's icon, reserve space for them and then they will be
    absolutely positioned.

    Icons for inputs are always 24px sized
  */
  &.icon-left {
    padding-left: ${rem(48)};
  }

  &.icon-right {
    padding-right: ${rem(48)};
  }
`

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  getInvalidMessage?: (input: HTMLInputElement) => string | undefined;
  inputClass?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  containerRef?: React.Ref<HTMLDivElement>;
  alphaOnly?: boolean;
  // Override the 'required' error message
  requiredErrorMessage?: string;
  // Override any other validation error
  // if you want more granular error handling, use getInvalidMessage
  invalidErrorMessage?: string;
  manualError?: boolean;
  noValidationSpacing?: boolean;
  noValidationMessage?: boolean;
  onInputValidate?: (input: HTMLInputElement) => void;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  uppercase?: boolean;
  helpText?: string;
}

interface State {
  isDirty?: boolean;
  error?: string;
}

const alphaOnlyRegex = '^[a-zA-Z\ \-\']+$'

class TextInputOnly extends React.Component<React.PropsWithChildren<Props>, State> {
  state: State = {
    isDirty: !!(this.props.defaultValue || this.props.value),
  }

  public static defaultProps = {
    type: 'text',
    getInvalidMessage: (_input: HTMLInputElement) => '',
  }

  setDirty = () => {
    if (!this.state.isDirty) {
      this.setState({ isDirty: true })
    }
  }

  reset = () => {
    if (this.state.isDirty) {
      this.setState({ isDirty: false })
    }
  }

  updateErrorMessage = (input: HTMLInputElement) => {
    const { error } = this.state
    const {
      requiredErrorMessage,
      invalidErrorMessage,
      getInvalidMessage,
      alphaOnly,
      maxLength,
      minLength,
      type,
      min,
      max,
      onInputValidate,
    } = this.props
    const validity = onInputValidate ? onInputValidate(input) : input.validity
    if (validity && !validity.valid) {
      let message = getInvalidMessage?.(input)
      if (!message) {
        if (validity.valueMissing && requiredErrorMessage) {
          message = requiredErrorMessage
        } else if (invalidErrorMessage) {
          message = invalidErrorMessage
        } else if (validity.patternMismatch && type === 'tel') {
          message = 'Phone number can contain numbers only'
        } else if (validity.patternMismatch && alphaOnly) {
          message = 'Please enter letters only'
        } else if (validity.tooShort) {
          message = `Please enter at least ${minLength} characters`
        } else if (validity.tooLong) {
          message = `Please enter at most ${maxLength} characters`
        } else if (type === 'date' && validity.rangeUnderflow) {
          message = `Must be after ${moment(min).format(DMY_DATE_FORMAT)}`
        } else if (type === 'date' && validity.rangeOverflow) {
          message = `Must be before ${moment(max).format(DMY_DATE_FORMAT)}`
        } else if (type === 'date' && validity.badInput) {
          message = 'Invalid date'
        } else {
          message = input.validationMessage
        }
      }
      this.setState({ error: message })
    } else if (error) {
      this.setState({ error: undefined })
    }
  }

  render() {
    const {
      className,
      children,
      inputClass,
      disabled,
      inputRef,
      alphaOnly,
      pattern,
      style,
      startIcon,
      endIcon,
      noValidationSpacing,
      noValidationMessage,
      onInputValidate,
      manualError,
      containerRef,
      getInvalidMessage,
      requiredErrorMessage,
      invalidErrorMessage,
      helpText,
      ...inputProps
    } = this.props
    const { isDirty, error } = this.state

    let errorMsg = isDirty ? error : undefined

    if (manualError) {
      errorMsg = invalidErrorMessage
    }

    return (
      <InputWrap
        ref={containerRef}
        className={className}
        error={errorMsg}
        disabled={disabled}
        noValidationSpacing={noValidationSpacing}
        noValidationMessage={noValidationMessage}
        startIcon={startIcon}
        endIcon={endIcon}
        helpText={helpText}
      >
        <StyledInput
          {...inputProps}
          pattern={alphaOnly ? alphaOnlyRegex : pattern}
          ref={inputRef}
          disabled={disabled}
          className={cn(inputClass,
            { 'icon-left': !!startIcon, 'icon-right': !!endIcon },
          )}
          onErrorUpdate={this.updateErrorMessage}
          onDirtyChange={this.setDirty}
        />
        {children}
      </InputWrap>
    )
  }
}

export default TextInputOnly
